import { parse } from "json2csv";
import { NextPageWithLayout } from "next";
import { useRouter } from "next/router";
import { ReactElement, useEffect, useState } from "react";

import PlusIcon from "/public/icon/plus.svg";

import {
  IndexReservation,
  OrderSettlementMethod,
  OrderStatus,
  Pagination,
  ReservationCsv,
  ServiceType,
  SortDirection,
} from "@/apis/clients";
import { ApiResponseError, ApiValidationError } from "@/apis/errors";
import reservationApi from "@/apis/reservation/reservationApi";
import { Button } from "@/components/atoms/buttons/Button";
import { ButtonWithIcon } from "@/components/atoms/buttons/ButtonWithIcon";
import SearchField from "@/components/atoms/inputForms/SearchField";
import { PageHeader } from "@/components/organisms/header/PageHeader";
import { NoPostMessage } from "@/components/organisms/not-found/NoPostMessage";
import { ReservationSearchField } from "@/components/organisms/reservations/index/ReservationSearchField";
import { ReservationSearchModal } from "@/components/organisms/reservations/index/ReservationSearchModal";
import ReservationTable from "@/components/organisms/reservations/index/ReservationTable";
// import { PaymentConfirmModal } from "@/components/organisms/reservations/show/PaymentConfirmModal";
import { LayoutTemplate } from "@/components/templates/layout";
import { useSystemMessage } from "@/hooks/systemMessage";
import useApiResponseErrorHandler from "@/hooks/useApiResponseErrorHandler";
import { ReservationSearchParams } from "@/models/reservation/reservation";
import { useLoading } from "@/states/loading/operations";
import { downloadCsv } from "@/utils/common";
import { NEXT_URL } from "@/utils/const";
import { some, orderBy } from "lodash";

const title = "申込";
const pageTitle = "申込一覧";

export interface IFieldCsv {
  label: string;
  value: string | ((reservation: ReservationCsv) => string);
}

export interface IDynamicField {
  key: string;
  label: string;
  service_id: number;
  service_custom_form_id: number;
  value: string;
}

export interface IDynamicFieldCsv {
  key: string;
  label: string;
  service_id: number;
  service_custom_form_id: number;
  value: (reservation: ReservationCsv) => string;
}

export const dynamicCustomValue = (
  field: string,
  reservation: ReservationCsv,
): string => {
  return (
    reservation.service_custom_values
      ?.filter(
        (obj) => `${obj.service_id}_${obj.service_custom_form_id}` === field,
      )
      ?.map((obj) => obj.input_value)
      .join("\n") ?? ""
  );
};

const csvFields: IFieldCsv[] = [
  { label: "注文番号", value: "order_id" },
  { label: "サービス名", value: "service_name" },
  { label: "種類", value: "order_type" },
  { label: "チケット数", value: "quantity" },
  { label: "サービスメニュー名", value: "service_menu_name" },
  { label: "決済", value: "settlement_method" },
  { label: "合計金額", value: "purchase_price" },
  { label: "参加日時", value: "event_dates" },
  { label: "個別金額", value: "detail_prices" },
  { label: "オプション申し込み", value: "option_names" },
  { label: "オプション金額", value: "option_prices" },
  { label: "姓", value: "last_name" },
  { label: "名", value: "first_name" },
  { label: "セイ", value: "last_name_kana" },
  { label: "メイ", value: "first_name_kana" },
  { label: "Email", value: "email" },
  { label: "サブEmail", value: "sub_email" },
  { label: "電話番号", value: "tel" },
  { label: "郵便番号", value: "zip" },
  { label: "都道府県", value: "pref_name" },
  { label: "住所１", value: "address1" },
  { label: "住所２", value: "address2" },
  { label: "生年月日", value: "birthday" },
  { label: "年齢", value: "age" },
  { label: "性別", value: "gender" },
  { label: "顧客業種名", value: "industry_name" },
  { label: "業種 その他", value: "industry_text" },
  { label: "会社名", value: "company_name" },
  { label: "部署名", value: "department" },
  { label: "役職", value: "official_position" },
  { label: "HP", value: "hp" },
  { label: "LINE ID", value: "line_id" },
  { label: "Facebook ID", value: "facebook_id" },
  { label: "twitter ID", value: "twitter_id" },
  { label: "instagram ID", value: "instagram_id" },
  { label: "メモ", value: "memo" },
  { label: "メール送信設定", value: "send_mail_flag" },
  { label: "作成日時", value: "created_at" },
  { label: "更新日時", value: "updated_at" },
];

const ReservationIndex: NextPageWithLayout = () => {
  const router = useRouter();
  const query = router.query;
  const withLoading = useLoading();
  const systemMessage = useSystemMessage();
  const { handlePageError } = useApiResponseErrorHandler();
  const [isOpenSearchModal, setIsOpenSearchModal] = useState<boolean>(false);
  const [isOpenPaymentModal, setIsOpenPaymentModal] = useState<boolean>(false);
  const [selected, setSelected] = useState<number>(-1);

  const [reservations, setReservations] = useState<IndexReservation[]>([]);
  const [pagination, setPagination] = useState<Pagination>({
    current: 0,
    next: 0,
    prev: 0,
    last: 0,
    per: 0,
    total: 0,
  });
  const [defaultParams, setDefaultParams] = useState<ReservationSearchParams>(
    {},
  );
  const [newParams, setNewParams] = useState<ReservationSearchParams>({});
  // 検索条件設定されているかチェックするフラグ.
  const [isCheckCondition, setCheckCondition] = useState<boolean>(false);

  const handleSearch = async () => {
    const query: { [k: string]: number | string | undefined } = {
      page: 1,
    };
    Object.keys(newParams).forEach((k) => {
      query[k] = newParams[k as keyof ReservationSearchParams];
    });
    Object.assign(query, {
      page: 1,
    });
    checkSearchCondition();
    await router.push({
      pathname: "/reservations",
      query,
    });
  };

  const handleClear = () => {
    setNewParams({
      // service_type: ServiceType.EVENT_SEMINAR_FREE,
      sort: newParams.sort,
    });
  };

  const handleOpenSearchModal = () => {
    setIsOpenSearchModal(true);
  };

  const handleCancelSearchModal = () => {
    setIsOpenSearchModal(false);
  };

  const handleClickPaymentConfirm = (id: number) => {
    setSelected(id);
    setIsOpenPaymentModal(true);
  };

  const handleClosePaymentConfirm = () => {
    setSelected(-1);
    setIsOpenPaymentModal(false);
  };

  const handleClickSort = async (direction: SortDirection) => {
    const query: { [k: string]: number | string | undefined } = {
      page: 1,
    };
    Object.keys(defaultParams).forEach((k) => {
      query[k] = defaultParams[k as keyof ReservationSearchParams];
    });
    Object.assign(query, {
      sort: direction,
    });
    await router.push({
      pathname: "/reservations",
      query,
    });
  };

  const handleClickCsv = (): void => {
    const { page, ...omit } = defaultParams;
    const downloadCsvParams = {
      isAll,
      // ids: checkedIds,
      ...omit,
    };
    withLoading(async () => {
      const res = await reservationApi.csvReservation({
        isAll: downloadCsvParams.isAll,
        text: downloadCsvParams.text,
        hopedReserveDateFrom: downloadCsvParams.hoped_reserve_date_from,
        hopedReserveDateTo: downloadCsvParams.hoped_reserve_date_to,
        serviceType: downloadCsvParams.service_type,
        serviceId: downloadCsvParams.service_id,
        serviceMenuId: downloadCsvParams.service_menu_id,
        serviceName: downloadCsvParams.service_name,
        amountFrom: downloadCsvParams.amount_from,
        amountTo: downloadCsvParams.amount_to,
        orderStatus: downloadCsvParams.order_status,
        paymentMethod: downloadCsvParams.payment_method,
        sort: downloadCsvParams.sort,
      });
      const dynamicFields: IDynamicField[] = [];
      res.data?.forEach((item) => {
        item.service_custom_values?.forEach((obj) => {
          const __key = `${obj.service_id}_${obj.service_custom_form_id}`;

          if (!some(dynamicFields, { key: __key })) {
            dynamicFields.push({
              key: __key,
              label: obj.form_label,
              service_id: obj.service_id,
              service_custom_form_id: obj.service_custom_form_id,
            } as IDynamicField);
          }
        });
      });

      const dynamicFieldResult: IDynamicFieldCsv[] = orderBy(
        dynamicFields,
        ["service_custom_form_id"],
        ["desc"],
      )?.map((obj) => {
        return {
          ...obj,
          value: (reservation: ReservationCsv) =>
            dynamicCustomValue(obj.key, reservation),
        };
      });

      const csvFieldsExport: IFieldCsv[] = [...csvFields];

      dynamicFieldResult?.forEach((dynamic: IDynamicFieldCsv) => {
        csvFieldsExport.push({ label: dynamic.label, value: dynamic.value });
      });
      const csv = parse(res.data, { fields: csvFieldsExport, withBOM: true });
      downloadCsv(new Blob([csv]), "yoyaku_list.csv");
    }).catch((e) => {
      if (e instanceof ApiResponseError) {
        void handlePageError(e);
      }
    });
  };

  const handlePaymentConfirmSubmit = async () => {
    if (selected < 0) return;
    await withLoading(async () => {
      try {
        await reservationApi.updateReservationComplete({
          id: selected,
        });
        fetchReservation();
        handleClosePaymentConfirm();
        systemMessage.info("ステータスの更新に成功しました");
      } catch (e) {
        if (e instanceof ApiValidationError) {
          let errors: string[] = [];
          for (const key in e.errors) {
            errors = errors.concat(e.errors[key]);
          }
          systemMessage.danger(errors.join(" "));
        }

        if (e instanceof ApiResponseError) {
          await handlePageError(e);
        }
      }
    });
  };

  const handleEditReservation = (id: number) => {
    void router.push({
      pathname: NEXT_URL.RESERVATIONS_EDIT,
      query: { id },
    });
  };

  const handleClickShow = (id: number) => {
    void router.push({
      pathname: NEXT_URL.RESERVATIONS_DETAIL,
      query: { id },
    });
  };

  const [isAll, setIsAll] = useState<boolean>(true);
  const [checkedIds, setCheckedIds] = useState<number[]>([]);
  const [unCheckedIds, setUnCheckedIds] = useState<number[]>([]);

  const handleClickAll = () => {
    if (isAll) {
      // 「isAll」状態の場合、チェックが外れているものがあれば、ページを跨いで「全選択」し直す
      setIsAll(!(unCheckedIds.length === 0));
    } else {
      // 「isAll」状態でない場合、ページを跨いで「全選択」する
      setIsAll(true);
    }
    setCheckedIds([]);
    setUnCheckedIds([]);
  };

  const handleClickCheckbox = (id: number) => {
    // 「全選択」状態の場合
    if (isAll) {
      if (unCheckedIds.includes(id)) {
        setUnCheckedIds((prevState) =>
          prevState.filter((unCheckedId) => unCheckedId !== id).sort(),
        );
      } else {
        // 手動で全てチェックを外した場合
        if (unCheckedIds.length === pagination.total - 1) {
          setIsAll(false);
          setUnCheckedIds([]);
          return;
        }
        setUnCheckedIds((prevState) => prevState.concat(id).sort());
      }
      return;
    }

    if (checkedIds.includes(id)) {
      setCheckedIds((prevState) =>
        prevState.filter((checkedId) => checkedId !== id).sort(),
      );
    } else {
      if (checkedIds.length === pagination.total - 1) {
        // 手動で全てチェックを入れた場合
        setIsAll(true);
        setCheckedIds([]);
        return;
      }
      setCheckedIds((prevState) => prevState.concat(id).sort());
    }
  };

  const fetchReservation = () => {
    const searchParams: ReservationSearchParams = {};
    if (query.text) {
      searchParams.text = String(query.text);
    }
    if (query.hoped_reserve_date_from) {
      searchParams.hoped_reserve_date_from = String(
        query.hoped_reserve_date_from,
      );
    }
    if (query.hoped_reserve_date_to) {
      searchParams.hoped_reserve_date_to = String(query.hoped_reserve_date_to);
    }
    if (query.service_type) {
      searchParams.service_type = Number(query.service_type) as ServiceType;
    }
    if (query.service_id) {
      searchParams.service_id = Number(query.service_id);
    }
    if (query.service_menu_id) {
      searchParams.service_menu_id = Number(query.service_menu_id);
    }
    if (query.service_name) {
      searchParams.service_name = String(query.service_name);
    }
    if (query.amount_from) {
      searchParams.amount_from = Number(query.amount_from);
    }
    if (query.amount_to) {
      searchParams.amount_to = Number(query.amount_to);
    }
    if (query.order_status) {
      searchParams.order_status = Number(query.order_status) as OrderStatus;
    }
    if (query.payment_method) {
      searchParams.payment_method = Number(
        query.payment_method,
      ) as OrderSettlementMethod;
    }
    if (query.sort) {
      searchParams.sort = String(query.sort) as SortDirection;
    } else {
      searchParams.sort = SortDirection.Desc;
    }
    if (query.page) {
      searchParams.page = Number(query.page) ?? 1;
    }
    if (query.per) {
      searchParams.per_page = Number(query.per);
    }
    setDefaultParams(searchParams);
    setNewParams(searchParams);

    Promise.resolve(
      withLoading(async () => {
        const reservationResult = await reservationApi.indexReservation({
          text: searchParams.text,
          hopedReserveDateFrom: searchParams.hoped_reserve_date_from,
          hopedReserveDateTo: searchParams.hoped_reserve_date_to,
          serviceType: searchParams.service_type,
          serviceId: searchParams.service_id,
          serviceMenuId: searchParams.service_menu_id,
          serviceName: searchParams.service_name,
          amountFrom: searchParams.amount_from,
          amountTo: searchParams.amount_to,
          orderStatus: searchParams.order_status,
          paymentMethod: searchParams.payment_method,
          sort: searchParams.sort,
          page: searchParams.page,
          perPage: searchParams.per_page,
        });
        setReservations(reservationResult.data);
        setPagination(reservationResult.pagination);
      }),
    ).catch((e) => {
      if (e instanceof ApiResponseError) {
        void handlePageError(e);
      }
    });
  };

  useEffect(() => {
    if (router.isReady) {
      fetchReservation();
    }
  }, [query, router]);

  // 検索条件が設定されているかチェックする.
  // 設定されていた場合、検索結果0件だと指定の条件ではない表示が必要なため
  const checkSearchCondition = () => {
    if (
      newParams.text ||
      newParams.amount_from ||
      newParams.amount_to ||
      newParams.hoped_reserve_date_from ||
      newParams.hoped_reserve_date_to ||
      newParams.order_status ||
      newParams.payment_method ||
      newParams.service_name ||
      newParams.service_id ||
      newParams.service_type ||
      newParams.service_menu_id
    ) {
      setCheckCondition(true);
      return;
    }
    setCheckCondition(false);
  };

  return (
    <>
      <ReservationSearchModal
        isOpen={isOpenSearchModal}
        close={handleCancelSearchModal}
        newParams={newParams}
        setNewParams={setNewParams}
        handleClear={handleClear}
        handleSearch={handleSearch}
      />
      {/* {isOpenPaymentModal && (
        <PaymentConfirmModal
          method={3}
          isOpen={isOpenPaymentModal}
          handleCancel={handleClosePaymentConfirm}
          handleSubmit={handlePaymentConfirmSubmit}
        />
      )} */}
      <article>
        <div className="justify-between items-center mb-2 md:mb-0">
          <PageHeader title={pageTitle} />
        </div>
        <nav className="py-6 px-8 bg-theme-thin rounded-lg md:mt-10">
          <div className="hidden space-y-6 md:block">
            <ReservationSearchField
              newParams={newParams}
              setNewParams={setNewParams}
              handleClear={handleClear}
              handleSearch={handleSearch}
            />
          </div>
          <div className="block space-y-2 md:hidden">
            <SearchField
              value={newParams.text ?? ""}
              onChange={(e) => {
                setNewParams((prevState) => ({
                  ...prevState,
                  text: e.target.value,
                }));
              }}
              placeholder="顧客氏名、メールアドレス、電話番号、注文番号"
            />
            <div className="flex justify-between items-center">
              <ButtonWithIcon
                label="詳細検索"
                color="transparent"
                size="sm"
                onClick={handleOpenSearchModal}
                icon={<PlusIcon className="w-6 h-6 fill-theme" />}
              />
              <Button
                type="button"
                color="primary"
                size="sm"
                label="検索"
                onClick={handleSearch}
              />
            </div>
          </div>
        </nav>
        <section>
          {reservations.length <= 0 ? (
            <>
              {!isCheckCondition ? (
                <NoPostMessage text="まだ予約・申込はありません" />
              ) : (
                <NoPostMessage text="指定の条件は見つかりませんでした" />
              )}
            </>
          ) : (
            <ReservationTable
              unCheckedIds={unCheckedIds}
              checkedIds={checkedIds}
              isAll={isAll}
              handleClickAll={handleClickAll}
              handleClickCheckbox={handleClickCheckbox}
              reservations={reservations}
              handleEditReservation={handleEditReservation}
              handleClickShow={handleClickShow}
              handleClickSort={(direction) => {
                void handleClickSort(direction);
              }}
              sortState={defaultParams.sort}
              pagination={pagination}
              handleClickCsv={handleClickCsv}
              handleClickPaymentConfirm={handleClickPaymentConfirm}
              handlePagination={async (page, per) => {
                await router.push({
                  pathname: NEXT_URL.RESERVATIONS,
                  query: {
                    ...defaultParams,
                    page,
                    per,
                  },
                });
              }}
              handleRefreshReservations={fetchReservation}
            />
          )}
        </section>
      </article>
    </>
  );
};

export default ReservationIndex;

ReservationIndex.getLayout = function getLayout(children: ReactElement) {
  return <LayoutTemplate title={title}>{children}</LayoutTemplate>;
};
